import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  FileText, CheckCircle2, Clock, XCircle, MinusCircle, ShieldCheck,
  PenLine, Eye, Loader2,
} from 'lucide-react';

import Button from '../../../components/ui/Button';
import PictureUpload from '../../../components/ui/PictureUpload';
import DocumentViewerModal from '../../../components/ui/DocumentViewerModal';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${({ $rejected }) =>
    ($rejected ? 'rgba(229,57,53,0.4)' : '#E8ECF0')};
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const IconWrap = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocTitle = styled.div`
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
`;

const RejectNote = styled.div`
  margin-top: 10px;
  background: rgba(229, 57, 53, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.auxiliar.danger};

  span { font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold}; }
`;

const FileLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.primary.main};
  &:hover { text-decoration: underline; }
`;

const UploadArea = styled.div`
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

// OB-F — açık rıza beyanı satırı. Metin küçük ama KAPI: kutu işaretlenmeden
// yükleme düğmesi açılmaz.
const ConsentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.dark.little};

  input {
    margin-top: 2px;
    accent-color: ${({ theme }) => theme.colors.primary.main};
    cursor: pointer;
  }

  label {
    cursor: pointer;
  }
`;

const Empty = styled.div`
  text-align: center;
  padding: 48px 16px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-family: ${({ theme }) => theme.metrics.font.family};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
`;

const statusVisual = (status) => {
  switch (status) {
    case 'approved': return { Icon: CheckCircle2, bg: 'rgba(2,211,191,0.12)', color: '#01A899' };
    case 'uploaded': return { Icon: Clock, bg: 'rgba(2,145,239,0.10)', color: '#0291EF' };
    case 'rejected': return { Icon: XCircle, bg: 'rgba(229,57,53,0.10)', color: '#E53935' };
    case 'waived':
    case 'cancelled': return { Icon: MinusCircle, bg: 'rgba(102,102,119,0.10)', color: '#667' };
    default: return { Icon: FileText, bg: 'rgba(255,193,7,0.14)', color: '#C79100' };
  }
};

export default function DocumentsTab({ documents, onUpload }) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState({}); // { [docId]: File }
  // OB-F — belge başına AÇIK rıza beyanı. Ürün bugüne dek her belgeyi KVKK
  // kategorisiyle etiketleyip rıza topladığını ima ediyor, hiçbir kayıt
  // üretmiyordu. Kutu işaretlenmeden yükleme düğmesi açılmaz: rıza, yüklemenin
  // yan etkisi değil ÖNKOŞULUDUR.
  const [consented, setConsented] = useState({}); // { [docId]: boolean }
  const [busyId, setBusyId] = useState(null);
  // { fileUrl, title } | null — açık olduğunda DocumentViewerModal'ı besler. `fileUrl` KAYITLI
  // depolama adresidir (artık özel sınıf — bkz. StoragePurposes.preboardingDocument), imzalı
  // adres DEĞİL; çözümü DocumentViewerModal kendisi `/v1/storage/view` üzerinden yapar (kardeş
  // sekme WelcomeContentTab.js ile aynı desen).
  const [viewing, setViewing] = useState(null);

  const setFile = (docId, file) => setPicked((prev) => ({ ...prev, [docId]: file }));

  const runUpload = async (doc) => {
    const file = picked[doc.id];
    if (!(file instanceof File)) return;
    if (!consented[doc.id]) return;
    setBusyId(doc.id);
    const ok = await onUpload(doc.id, file, doc.consentVersion || null, true);
    setBusyId(null);
    if (ok) {
      setPicked((prev) => ({ ...prev, [doc.id]: null }));
      setConsented((prev) => ({ ...prev, [doc.id]: false }));
    }
  };

  const list = Array.isArray(documents) ? documents : [];

  return (
    <Wrap>
      {list.length === 0 && <Empty>{t('myPreboarding.documents.empty')}</Empty>}

      {list.map((doc) => {
        const v = statusVisual(doc.status);
        const editable = doc.status === 'pending' || doc.status === 'rejected';
        const isBusy = busyId === doc.id;
        const file = picked[doc.id];

        return (
          <Card key={doc.id} $rejected={doc.status === 'rejected'}>
            <Row>
              <IconWrap $bg={v.bg} $color={v.color}>
                <v.Icon size={18} />
              </IconWrap>
              <Body>
                <DocTitle>{doc.title}</DocTitle>
                <Meta>
                  <MetaPill>{t(`myPreboarding.documents.type.${doc.documentType}`, { defaultValue: doc.documentType })}</MetaPill>
                  <Badge $bg={v.bg} $color={v.color}>
                    {t(`myPreboarding.documents.status.${doc.status}`, { defaultValue: doc.status })}
                  </Badge>
                  {doc.isMandatory && (
                    <Badge $bg="rgba(255,193,7,0.14)" $color="#C79100">
                      {t('myPreboarding.documents.mandatory')}
                    </Badge>
                  )}
                  {doc.requiresESignature && (
                    <MetaPill>
                      <PenLine size={13} />
                      {t('myPreboarding.documents.requiresESignature')}
                    </MetaPill>
                  )}
                  {doc.kvkkCategory && (
                    <MetaPill>
                      <ShieldCheck size={13} />
                      {/* OB-F — rozet artık bir İMA değil, KAYDIN kendisi.
                          `consentVersion` doluysa rıza gerçekten kayıtlıdır ve
                          sürümüyle gösterilir; boşsa yalnız veri kategorisi
                          söylenir ("rıza alındı" iddiası edilmez). */}
                      {doc.consentVersion
                        ? t('myPreboarding.documents.kvkkConsented', { version: doc.consentVersion })
                        : t(`myPreboarding.documents.kvkkCategory.${doc.kvkkCategory}`, {
                          defaultValue: t('myPreboarding.documents.kvkk'),
                        })}
                    </MetaPill>
                  )}
                </Meta>

                {doc.status === 'rejected' && doc.rejectedReason && (
                  <RejectNote>
                    <span>{t('myPreboarding.documents.rejectedReason')}:</span> {doc.rejectedReason}
                  </RejectNote>
                )}

                {doc.fileUrl && !editable && (
                  <FileLink
                    type="button"
                    data-testid="preboarding-document-view-trigger"
                    onClick={() => setViewing({ fileUrl: doc.fileUrl, title: doc.title })}
                  >
                    <Eye size={14} />
                    {t('myPreboarding.documents.viewUploaded')}
                  </FileLink>
                )}

                {editable && (
                  <UploadArea>
                    <PictureUpload
                      value={file || null}
                      onChange={(f) => setFile(doc.id, f)}
                      accept="image/*,application/pdf"
                      maxSize={10}
                    />
                    <ConsentRow>
                      <input
                        type="checkbox"
                        id={`kvkk-consent-${doc.id}`}
                        data-testid="kvkk-consent-checkbox"
                        checked={Boolean(consented[doc.id])}
                        onChange={(e) => setConsented((prev) => ({ ...prev, [doc.id]: e.target.checked }))}
                      />
                      <label htmlFor={`kvkk-consent-${doc.id}`}>
                        {t('myPreboarding.documents.kvkkConsentLabel', {
                          category: t(`myPreboarding.documents.kvkkCategory.${doc.kvkkCategory}`, {
                            defaultValue: doc.kvkkCategory || '',
                          }),
                        })}
                      </label>
                    </ConsentRow>
                    <Actions>
                      <Button
                        variant="primary"
                        disabled={isBusy || !(file instanceof File) || !consented[doc.id]}
                        onClick={() => runUpload(doc)}
                      >
                        {isBusy ? <Loader2 size={16} /> : t('myPreboarding.documents.action.upload')}
                      </Button>
                    </Actions>
                  </UploadArea>
                )}
              </Body>
            </Row>
          </Card>
        );
      })}

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.title}
        onClose={() => setViewing(null)}
      />
    </Wrap>
  );
}
